import type { PageKey, PermissionDomain, PermissionPayload, PermissionRequirement } from "@hawk/shared";
import {
  DEFAULT_PERMISSIONS,
  PAGE_REQUIREMENTS,
  getPermissionPolicy,
  hasRequiredAccess,
  hasRequiredScopeAccess,
  normalizePermissionPayload,
} from "@hawk/shared";
import { getConnectedRoutePageKey } from "./navigation-routes";

type UserLike = {
  role?: string | null;
  permissions?: unknown;
} | null;

export type { AccessLevel, PageKey, PermissionDomain, PermissionPayload, PermissionRequirement } from "@hawk/shared";

const DEFAULT_PAGE_ORDER: PageKey[] = [
  "dashboard",
  "visits",
  "badges",
  "badgeIssuance",
  "badgeInitialEnrollment",
  "badgeRequests",
  "people",
  "devices",
  "reports",
  "settings",
  "settingsCatalog",
  "accessProfiles",
  "settingsOrganizations",
  "settingsBadgeTemplates",
  "settingsAdministration",
];

const PAGE_PATHS: Record<PageKey, string> = {
  dashboard: "/dashboard",
  visits: "/visits",
  badges: "/badges/permanent",
  badgeIssuance: "/badges/production/finalize",
  badgeInitialEnrollment: "/badges/initial-enrollment",
  badgeRequests: "/badges/requests",
  people: "/directory/people",
  devices: "/access-control/terminals",
  hawkEye: "/hawk-eye",
  reports: "/reports",
  settings: "/settings",
  settingsCatalog: "/access-control/zones",
  accessProfiles: "/access-control/profiles",
  settingsOrganizations: "/administration/organizations",
  settingsBadgeTemplates: "/badges/templates",
  settingsAdministration: "/directory/users",
};

export function getUserPermissions(user: unknown): PermissionPayload {
  if (!user || typeof user !== "object") {
    return DEFAULT_PERMISSIONS;
  }

  const userRecord = user as Record<string, unknown>;
  return normalizePermissionPayload(userRecord.permissions, DEFAULT_PERMISSIONS);
}

export function satisfiesRequirement(user: UserLike, requirement: PermissionRequirement) {
  if (!user) {
    return false;
  }

  if (requirement.kind === "authenticated") {
    return true;
  }

  const permissions = getUserPermissions(user);

  if (requirement.kind === "admin") {
    return permissions.admin;
  }

  if (requirement.kind === "anyPermission") {
    return (
      permissions.admin ||
      requirement.checks.some((check) =>
        hasRequiredAccess(permissions[check.domain], check.minimum)
      )
    );
  }

  return permissions.admin || hasRequiredAccess(permissions[requirement.domain], requirement.minimum);
}

export function canAccessPage(user: UserLike, pageKey: PageKey) {
  const permissions = getUserPermissions(user);
  const policy = getPermissionPolicy(permissions);

  if (policy.shell === "SENTINEL") {
    return pageKey === "visits";
  }

  if (pageKey === "badges" && !policy.badgesPageVisible) {
    return false;
  }

  if (pageKey === "badgeIssuance" && !policy.badgesIssue) {
    return false;
  }

  if (
    pageKey === "badgeInitialEnrollment" &&
    !policy.badgeInitialEnrollmentManage &&
    !policy.badgeInitialEnrollmentExecute
  ) {
    return false;
  }

  if (pageKey === "hawkEye" && !policy.securityHawkEye && !permissions.admin && permissions.security !== "full") {
    return false;
  }

  return satisfiesRequirement(user, PAGE_REQUIREMENTS[pageKey]);
}

export function canAccessVisitorBadgeInventory(user: UserLike) {
  return getPermissionPolicy(getUserPermissions(user)).scope !== "ORGANIZATION";
}

export function getVisiblePageKeys(user: UserLike, pageKeys: PageKey[]) {
  return pageKeys.filter((pageKey) => canAccessPage(user, pageKey));
}

export function getDefaultAuthorizedPage(user: UserLike) {
  return DEFAULT_PAGE_ORDER.find((pageKey) => canAccessPage(user, pageKey)) ?? null;
}

export function getDefaultAuthorizedPath(user: UserLike) {
  const permissions = getUserPermissions(user);
  const policy = getPermissionPolicy(permissions);
  if (policy.badgesIssue && canAccessPage(user, "badgeIssuance")) {
    return "/badges/production/requests";
  }
  const shouldPreferBadgeRequests =
    permissions.admin ||
    policy.badgeRequestsInbox ||
    policy.badgeRequestsCreate !== "none" ||
    policy.badgeRequestsCancel !== "none";

  if (shouldPreferBadgeRequests && canAccessPage(user, "badgeRequests")) {
    return "/badges/requests";
  }

  const pageKey = getDefaultAuthorizedPage(user);
  return pageKey ? PAGE_PATHS[pageKey] : null;
}

export function getAuthorizedPostLoginPath(
  user: UserLike,
  returnTo: string | null,
) {
  if (returnTo) {
    try {
      const pathname = new URL(returnTo, "https://hawk.local").pathname;
      const pageKey = getConnectedRoutePageKey(pathname);
      if (pageKey && canAccessPage(user, pageKey)) {
        return returnTo;
      }
    } catch {
      // Ignore malformed return URLs and fall back to the role destination.
    }
  }

  return getDefaultAuthorizedPath(user);
}

export function canPerformAction(
  user: UserLike,
  requirement: PermissionRequirement | { domain: PermissionDomain; minimum: "read" | "full" }
) {
  if ("kind" in requirement) {
    return satisfiesRequirement(user, requirement);
  }

  return satisfiesRequirement(user, {
    kind: "permission",
    domain: requirement.domain,
    minimum: requirement.minimum,
  });
}

export function canManagePeople(user: unknown) {
  return canPerformAction(user as UserLike, { domain: "people", minimum: "full" });
}

export function canManageGlobalPeople(user: unknown) {
  const userRecord = user as UserLike;
  const permissions = getUserPermissions(userRecord);
  const policy = getPermissionPolicy(permissions);
  return (
    (permissions.admin || hasRequiredAccess(permissions.people, "full")) &&
    policy.scope !== "ORGANIZATION"
  );
}

export function canEditPeopleDirectoryDetails(user: unknown) {
  return canPerformAction(user as UserLike, { domain: "people", minimum: "read" });
}

export function canManageBadges(user: unknown) {
  return canPerformAction(user as UserLike, { domain: "badges", minimum: "full" });
}

export function canManageExistingBadges(user: unknown) {
  const permissions = getUserPermissions(user);
  const policy = getPermissionPolicy(permissions);
  return permissions.admin || permissions.badges === "full" || policy.badgesManageExisting;
}

export function canManageBadgeRequests(user: unknown) {
  return canPerformAction(user as UserLike, { domain: "badgeRequests", minimum: "full" });
}

export function canManageVisits(user: unknown) {
  return canPerformAction(user as UserLike, { domain: "visits", minimum: "full" });
}

export function canCreateVisits(user: unknown) {
  const userRecord = user as UserLike;
  const permissions = getUserPermissions(userRecord);
  const policy = getPermissionPolicy(permissions);

  if (permissions.admin || canPerformAction(userRecord, { domain: "visits", minimum: "full" })) {
    return true;
  }

  return hasRequiredScopeAccess(policy.visitsCreate, "own");
}

export function canViewOwnVisits(user: unknown) {
  const permissions = getUserPermissions(user);
  return hasRequiredScopeAccess(getPermissionPolicy(permissions).visitsRead, "own");
}

export function canViewOrganizationVisits(user: unknown) {
  const permissions = getUserPermissions(user);
  return hasRequiredScopeAccess(getPermissionPolicy(permissions).visitsRead, "organization");
}

export function canEditOwnVisits(user: unknown) {
  const permissions = getUserPermissions(user);
  return hasRequiredScopeAccess(getPermissionPolicy(permissions).visitsEdit, "own");
}

export function hasPersonalVisitWorkflow(user: unknown) {
  const permissions = getUserPermissions(user);
  const policy = getPermissionPolicy(permissions);

  return (
    policy.shell !== "SENTINEL" &&
    permissions.visits !== "none" &&
    policy.visitsRead === "own" &&
    policy.visitsCreate === "own" &&
    policy.visitsEdit === "own"
  );
}

export function canViewSecurity(user: unknown) {
  return canPerformAction(user as UserLike, { domain: "security", minimum: "read" });
}

export function canManageSecurity(user: unknown) {
  return canPerformAction(user as UserLike, { domain: "security", minimum: "full" });
}

export function canViewDevices(user: unknown) {
  return canPerformAction(user as UserLike, { domain: "devices", minimum: "read" });
}

export function canManageDevices(user: unknown) {
  return canPerformAction(user as UserLike, { domain: "devices", minimum: "full" });
}

export function canViewSettings(user: unknown) {
  return canPerformAction(user as UserLike, { domain: "settings", minimum: "read" });
}

export function canManageSettings(user: unknown) {
  return canPerformAction(user as UserLike, { domain: "settings", minimum: "full" });
}

export function canUseVisitorBadgeReturn(user: unknown) {
  return getPermissionPolicy(getUserPermissions(user)).badgesReturnVisitor;
}

export function canViewOwnBadges(user: unknown) {
  const permissions = getUserPermissions(user);
  return hasRequiredScopeAccess(getPermissionPolicy(permissions).badgesRead, "own");
}

export function canIssueBadges(user: unknown) {
  const permissions = getUserPermissions(user);
  const policy = getPermissionPolicy(permissions);
  return permissions.admin || permissions.badges === "full" || policy.badgesIssue;
}

export function canManageInitialBadgeEnrollment(user: unknown) {
  return getPermissionPolicy(getUserPermissions(user)).badgeInitialEnrollmentManage;
}

export function canExecuteInitialBadgeEnrollment(user: unknown) {
  return getPermissionPolicy(getUserPermissions(user)).badgeInitialEnrollmentExecute;
}

export function canViewSensitiveSecurityEvents(user: unknown) {
  const permissions = getUserPermissions(user);
  const policy = getPermissionPolicy(permissions);
  return permissions.admin || policy.securityReadSensitive || permissions.security === "full";
}

export function canCreateBadgeRequests(user: unknown) {
  const permissions = getUserPermissions(user);
  return permissions.admin || getPermissionPolicy(permissions).badgeRequestsCreate !== "none";
}

export function canApproveOrgBadgeRequests(user: unknown) {
  const permissions = getUserPermissions(user);
  return permissions.admin || getPermissionPolicy(permissions).badgeRequestsApproveOrg;
}

export function canApproveCabinetBadgeRequests(user: unknown) {
  const permissions = getUserPermissions(user);
  return permissions.admin || getPermissionPolicy(permissions).badgeRequestsApproveCabinet;
}

export function canCancelBadgeRequests(user: unknown) {
  const permissions = getUserPermissions(user);
  return permissions.admin || getPermissionPolicy(permissions).badgeRequestsCancel !== "none";
}
